import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { CreditCard, Loader2 } from 'lucide-react';

/**
 * Interface for transaction data point
 */
export interface TransactionData {
  date: string;
  count: number;
}

interface TransactionChartProps {
  data: TransactionData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Custom tooltip component for transaction display
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700">{label}</p>
        <p className="text-cyan-600">
          Transactions: <span className="font-bold">{payload[0].value}</span>
        </p>
      </div>
    );
  }
  return null;
};

/**
 * TransactionChart - Bar chart showing daily transaction count
 *
 * Features:
 * - Displays daily transaction count
 * - Cyan/blue color scheme for financial data
 * - Gradient bar fill for modern look
 * - Interactive tooltips
 * - Responsive design for all screen sizes
 * - Loading and error states
 *
 * @param data - Array of daily transaction data points
 * @param loading - Loading state indicator
 * @param error - Error message if data fetch failed
 * @param height - Chart height in pixels (default: 350)
 */
const TransactionChart: React.FC<TransactionChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        <span className="ml-2 text-gray-600">Loading transaction data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No transaction data available</p>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalTransactions = data.reduce((sum, item) => sum + item.count, 0);
  const averageTransactions = (totalTransactions / data.length).toFixed(1);
  const peakDay = data.reduce((max, item) => item.count > max.count ? item : max, data[0]);

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <CreditCard className="w-5 h-5 mr-2 text-cyan-600" />
          Daily Transaction Activity
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Transaction count tracking over time
        </p>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-cyan-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total</p>
          <p className="text-lg font-bold text-cyan-600">{totalTransactions}</p>
        </div>
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Daily Average</p>
          <p className="text-lg font-bold text-blue-600">{averageTransactions}</p>
        </div>
        <div className="bg-indigo-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Peak Day</p>
          <p className="text-lg font-bold text-indigo-600">{peakDay.count}</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <defs>
            <linearGradient id="colorTransactions" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.9} />
              <stop offset="95%" stopColor="#0891b2" stopOpacity={0.7} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            stroke="#666"
            tick={{ fontSize: 12 }}
            tickMargin={10}
          />
          <YAxis
            stroke="#666"
            tick={{ fontSize: 12 }}
            allowDecimals={false}
            label={{ value: 'Transaction Count', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="rect"
          />
          <Bar
            dataKey="count"
            name="Daily Transactions"
            fill="url(#colorTransactions)"
            radius={[8, 8, 0, 0]}
            animationDuration={1000}
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Activity Indicator */}
      <div className="mt-4 bg-gradient-to-r from-cyan-50 to-blue-50 p-3 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">Transaction Volume</span>
          <div className="flex items-center">
            <div
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                totalTransactions > 100
                  ? 'bg-green-100 text-green-700'
                  : totalTransactions > 50
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {totalTransactions > 100 ? 'High' : totalTransactions > 50 ? 'Medium' : 'Low'} Activity
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionChart;
